import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { analyticsApi, ordersApi } from '../../services/api';
import { mockSalesData } from '../../data/salesMock';
import { DollarSign, ShoppingCart, TrendingUp, Users, ArrowUpRight, ArrowDownRight, Package, Loader2, ExternalLink } from 'lucide-react';

const COLORS = ['#2d4a47', '#5a8a84', '#8fc4bc', '#c5e4df'];

const SalesSection = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [salesData, setSalesData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [salesRes, summaryRes, ordersRes] = await Promise.all([
          analyticsApi.getSales(),
          analyticsApi.getSummary(),
          ordersApi.getAll(10, 0) // Get 10 most recent orders
        ]);
        setSalesData(salesRes.data);
        setSummary(summaryRes.data);
        setRecentOrders(ordersRes.data || []);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        // Use mock data as fallback
        setSalesData(mockSalesData);
        setSummary(mockSalesData.summary);
        setRecentOrders(mockSalesData.recentOrders || []);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#2d4a47]" />
      </div>
    );
  }

  const { dailySales, monthlySales, productSales } = salesData || mockSalesData;

  const formatCurrency = (value) => `$${value.toLocaleString()}`;
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate totals for the displayed range
  const last7Days = dailySales.slice(-7);
  const last7DaysRevenue = last7Days.reduce((sum, day) => sum + day.revenue, 0);
  const last7DaysOrders = last7Days.reduce((sum, day) => sum + day.orders, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.totalRevenue)}</p>
              <div className="flex items-center mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <span className="text-green-500 font-medium">{summary.revenueGrowth}%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-[#2d4a47]/10 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#2d4a47]" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalOrders.toLocaleString()}</p>
              <div className="flex items-center mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <span className="text-green-500 font-medium">{summary.ordersGrowth}%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-[#2d4a47]/10 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-[#2d4a47]" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Avg. Order Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.averageOrderValue)}</p>
              <div className="flex items-center mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <span className="text-green-500 font-medium">2.3%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-[#2d4a47]/10 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#2d4a47]" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Returning Customers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{summary.returningCustomers}%</p>
              <div className="flex items-center mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <span className="text-green-500 font-medium">5.1%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-[#2d4a47]/10 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-[#2d4a47]" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Overview</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setTimeRange('7d')}
                className={`px-3 py-1 text-sm rounded ${timeRange === '7d' ? 'bg-[#2d4a47] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                7 Days
              </button>
              <button
                onClick={() => setTimeRange('30d')}
                className={`px-3 py-1 text-sm rounded ${timeRange === '30d' ? 'bg-[#2d4a47] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                30 Days
              </button>
              <button
                onClick={() => setTimeRange('12m')}
                className={`px-3 py-1 text-sm rounded ${timeRange === '12m' ? 'bg-[#2d4a47] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                12 Months
              </button>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {timeRange === '12m' ? (
                <BarChart data={monthlySales}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#2d4a47" radius={[4, 4, 0, 0]} />
                </BarChart>
              ) : (
                <AreaChart data={timeRange === '7d' ? last7Days : dailySales}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2d4a47" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2d4a47" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#2d4a47" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Product Sales Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Sales by Product</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={productSales}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {productSales.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Share']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {productSales.map((product, index) => (
              <div key={product.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                  <span className="text-gray-600">{product.name}</span>
                </div>
                <span className="font-medium">{formatCurrency(product.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Orders Trend and Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders Trend */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Orders Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailySales.slice(-14)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => [value, 'Orders']}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Line type="monotone" dataKey="orders" stroke="#2d4a47" strokeWidth={2} dot={{ fill: '#2d4a47', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">This Month</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#2d4a47]/5 p-4 rounded-lg">
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-xl font-bold text-[#2d4a47] mt-1">{formatCurrency(summary.thisMonthRevenue)}</p>
            </div>
            <div className="bg-[#2d4a47]/5 p-4 rounded-lg">
              <p className="text-sm text-gray-500">Orders</p>
              <p className="text-xl font-bold text-[#2d4a47] mt-1">{summary.thisMonthOrders}</p>
            </div>
            <div className="bg-[#2d4a47]/5 p-4 rounded-lg">
              <p className="text-sm text-gray-500">Last 7 Days Revenue</p>
              <p className="text-xl font-bold text-[#2d4a47] mt-1">{formatCurrency(last7DaysRevenue)}</p>
            </div>
            <div className="bg-[#2d4a47]/5 p-4 rounded-lg">
              <p className="text-sm text-gray-500">Last 7 Days Orders</p>
              <p className="text-xl font-bold text-[#2d4a47] mt-1">{last7DaysOrders}</p>
            </div>
          </div>
          <div className="mt-6">
            <p className="text-sm text-gray-500 mb-2">Conversion Rate</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div className="bg-[#2d4a47] h-2 rounded-full" style={{ width: `${summary.conversionRate * 10}%` }} />
              </div>
              <span className="text-sm font-medium">{summary.conversionRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders Table */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
          <button 
            onClick={() => navigate('/admin/orders')}
            className="text-sm text-[#2d4a47] hover:underline flex items-center gap-1"
          >
            View All Orders <ExternalLink size={14} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-gray-500">Order ID</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Customer</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Items</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Total</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <tr 
                    key={order.id} 
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/orders/${order.id}`)}
                  >
                    <td className="py-3 px-4 font-mono text-[#2d4a47]">
                      {order.id?.substring(0, 8)}...
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : order.date}
                    </td>
                    <td className="py-3 px-4 text-gray-900">
                      {order.customer?.firstName 
                        ? `${order.customer.firstName} ${order.customer.lastName || ''}`
                        : (typeof order.customer === 'object' ? (order.customer?.email || 'N/A') : (order.customer || 'N/A'))
                      }
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {order.items?.length || order.items || 0} item(s)
                    </td>
                    <td className="py-3 px-4 font-medium">
                      ${typeof order.total === 'number' ? order.total.toFixed(2) : order.total}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'delivered' || order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'shipped' || order.status === 'Shipped' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'cancelled' || order.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                        order.status === 'refunded' || order.status === 'Refunded' ? 'bg-purple-100 text-purple-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {order.status || 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesSection;
